/**
 * EmailJS Configuration and Integration
 * 
 * SETUP INSTRUCTIONS:
 * 1. Sign up at https://www.emailjs.com/ (free tier: 200 emails/month)
 * 2. Create an email service (Gmail, Outlook, SendGrid, etc.)
 * 3. Create an email template with these variables:
 *    - {{to_email}} - recipient email address (IMPORTANT: Set this in "To Email" field!)
 *    - {{product_title}} - product name
 *    - {{product_link}} - product URL
 * - {{product_image}} - product image URL
 *    - {{message}} - optional message from sender
 *    - {{from_store}} - store domain
 *    - {{sender_email}} - email of the person sending the hint (optional)
 * 4. IMPORTANT: In your EmailJS template settings:
 *    - Set "To Email" field to: {{to_email}}
 *    - Set "Subject" to: Product Hint: {{product_title}}
 * 5. Replace the placeholder values below with your actual credentials
 * 6. Add the EmailJS SDK script to your theme:
 *    <script src="https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js"></script>
 */

(function() {
  'use strict';

  // ============================================
  // EMAILJS CONFIGURATION - REPLACE WITH YOUR VALUES
  // ============================================
  const EMAILJS_CONFIG = {
    // Your EmailJS Service ID (found in EmailJS dashboard > Email Services)
    SERVICE_ID: 'service_0tda32e',
    
    // Your EmailJS Template ID (found in EmailJS dashboard > Email Templates)
    TEMPLATE_ID: 'template_jgvr5th',
    
    // Your EmailJS Public Key (found in EmailJS dashboard > Account > API Keys)
    PUBLIC_KEY: 'CcTNRsohEEubggcLp'
  };

  // ============================================
  // EMAILJS INTEGRATION
  // ============================================
  
  /**
   * Check if EmailJS is properly configured
   * @returns {boolean}
   */
  function isEmailJSConfigured() {
    return EMAILJS_CONFIG.SERVICE_ID !== 'YOUR_SERVICE_ID' && 
           EMAILJS_CONFIG.TEMPLATE_ID !== 'YOUR_TEMPLATE_ID' && 
           EMAILJS_CONFIG.PUBLIC_KEY !== 'YOUR_PUBLIC_KEY' &&
           typeof emailjs !== 'undefined';
  }

  /**
   * Initialize EmailJS with the public key
   */
  function initEmailJS() {
    if (!isEmailJSConfigured()) {
      console.warn('EmailJS is not properly configured. Please check your credentials in emailjs-config.js');
      return false;
    }

    try {
      // Initialize EmailJS if not already initialized
      if (typeof emailjs !== 'undefined' && emailjs.init) {
        emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
        return true;
      } else {
        console.error('EmailJS SDK not loaded. Please add the EmailJS script to your theme.');
        return false;
      }
    } catch (error) {
      console.error('Error initializing EmailJS:', error);
      return false;
    }
  }

  /**
   * Send a hint email using EmailJS
   * @param {Object} params - Email parameters
   * @param {string} params.toEmail - Recipient email address
   * @param {string} params.productTitle - Product name
   * @param {string} params.productLink - Full product URL
   * @param {string} params.message - Optional message from sender
   * @param {string} params.productImage - Product image URL (optional)
   * @param {string} params.fromStore - Store domain
   * @param {string} params.senderEmail - Optional sender email
   * @returns {Promise} EmailJS send promise
   */
  async function sendHintEmail(params) {
    const {
      toEmail,
      productTitle,
      productLink,
      message = '',
      productImage = '',
      fromStore,
      senderEmail = '',
    } = params;

    // Validate required parameters
    if (!toEmail || !productTitle || !productLink || !fromStore) {
      const missing = [];
      if (!toEmail) missing.push('toEmail');
      if (!productTitle) missing.push('productTitle');
      if (!productLink) missing.push('productLink');
      if (!fromStore) missing.push('fromStore');
      throw new Error('Missing required email parameters: ' + missing.join(', '));
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(toEmail)) {
      throw new Error('Invalid recipient email address: ' + toEmail);
    }

    // Check if EmailJS is configured
    if (!isEmailJSConfigured()) {
      throw new Error('EmailJS is not properly configured. Please check your credentials in emailjs-config.js');
    }

    // Initialize EmailJS
    if (!initEmailJS()) {
      throw new Error('Failed to initialize EmailJS');
    }

    // Prepare template parameters
    const templateParams = {
      to_email: toEmail.trim(),
      product_title: productTitle,
      product_link: productLink,
      message: message || 'No message provided',
      from_store: fromStore,
      product_image: productImage || '',
      sender_email: senderEmail || ''
    };

    // Log what we're sending (for debugging)
    console.log('Sending EmailJS request:', {
      serviceId: EMAILJS_CONFIG.SERVICE_ID,
      templateId: EMAILJS_CONFIG.TEMPLATE_ID,
      toEmail: toEmail,
      templateParams: templateParams
    });

    // Send email using EmailJS
    try {
      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        {
          publicKey: EMAILJS_CONFIG.PUBLIC_KEY
        }
      );
      
      console.log('EmailJS response:', response);
      return response;
    } catch (error) {
      console.error('EmailJS send error:', error);
      
      // Provide more helpful error messages
      if (error.status === 422 && error.text && error.text.includes('recipients address')) {
        throw new Error('EmailJS Error: The recipient email address is empty. Please make sure:\n1. Your EmailJS template has "To Email" field set to: {{to_email}}\n2. The email address is valid: ' + toEmail);
      }
      
      if (error.text) {
        throw new Error('EmailJS Error: ' + error.text + ' (Status: ' + error.status + ')');
      }
      
      throw error;
    }
  }

  /**
   * Get EmailJS configuration status
   * @returns {Object} Configuration status
   */
  function getEmailJSStatus() {
    return {
      configured: isEmailJSConfigured(),
      serviceId: EMAILJS_CONFIG.SERVICE_ID,
      templateId: EMAILJS_CONFIG.TEMPLATE_ID,
      publicKey: EMAILJS_CONFIG.PUBLIC_KEY ? '***' + EMAILJS_CONFIG.PUBLIC_KEY.slice(-4) : 'Not set',
      sdkLoaded: typeof emailjs !== 'undefined'
    };
  }

  // ============================================
  // EXPOSE TO GLOBAL SCOPE
  // ============================================
  window.EmailJSHelper = {
    sendHintEmail: sendHintEmail,
    isConfigured: isEmailJSConfigured,
    init: initEmailJS,
    getStatus: getEmailJSStatus,
    config: EMAILJS_CONFIG
  };

  // Auto-initialize when EmailJS SDK is loaded
  if (typeof emailjs !== 'undefined') {
    initEmailJS();
  } else {
    // Wait for EmailJS SDK to load
    window.addEventListener('load', function() {
      if (typeof emailjs !== 'undefined') {
        initEmailJS();
      }
    });
  }

})();

